/**
 * IXVerify v2 — the two-tier capture uploader (ixv2 WP-3.2; plan §4, R11/R12).
 *
 * On every document capture two uploads leave the device:
 *   1. the downscaled RENDITION first — it drives extraction within ~1 s
 *      (R11: capture overlaps extraction, never serialized behind it);
 *   2. the untouched full-resolution ORIGINAL behind it, linked to its
 *      rendition via `link_asset_id`, hashed on arrival (R12: the certified
 *      copy). Attestation blocks on the ORIGINAL's arrival, not the
 *      rendition's — `pendingOriginalCount()` is that gate's client mirror.
 *
 * Per-asset queued/uploading/done/error chips; automatic retry with
 * exponential backoff on flaky networks, then a manual retry affordance.
 * Pure TS with injected dependencies (upload fn, timers) — unit-testable
 * without a DOM or a network.
 *
 * Cross-device step A (2026-09-18): the SERVER settles a page's index. Two
 * devices capturing into the same session both number pages from the same
 * counter seed, so the first tier to land can come back with a DIFFERENT
 * `page_index` than the one sent (a collision was bumped to max+1). The
 * uploader then remaps that page's tasks + pair to the effective index and
 * fires `onPageIndexReassigned(from, to)` so the wizard can move its local
 * preview; the original that follows inherits the index server-side anyway.
 */

export type UploadTier = 'original' | 'rendition';
export type UploadStatus = 'queued' | 'uploading' | 'done' | 'error';

export interface UploadTask {
  id: string;
  /** Human chip label, e.g. "Page 1", "Page 1 (original)". */
  label: string;
  tier: UploadTier;
  kind: 'ixrs_photo' | 'ixrs_document';
  status: UploadStatus;
  attempts: number;
  /** Server asset id once uploaded. */
  assetId: string | null;
  pageIndex: number;
  error: string | null;
}

export interface UploadResult {
  /** `page_index` is the EFFECTIVE index the server stored (may differ from
   *  the one sent — see the header). Optional so older seams still type. */
  asset: { id: string; page_index?: number | null };
}

export interface UploaderDeps {
  /** Perform one asset upload (the api.uploadSessionAsset seam). */
  upload: (
    blob: Blob,
    opts: {
      tier: UploadTier;
      kind: 'ixrs_photo' | 'ixrs_document';
      page_index: number;
      link_asset_id?: string;
      filename: string;
      source: 'camera' | 'upload';
    }
  ) => Promise<UploadResult>;
  /** Fired on any task state change with the full task list (UI chips). */
  onChange?: (tasks: UploadTask[]) => void;
  /** Fired once per page when its RENDITION lands — the extraction trigger (R11). */
  onRenditionUploaded?: (assetId: string, pageIndex: number) => void;
  /** Fired once per page when its ORIGINAL lands (unblocks attestation, R12). */
  onOriginalUploaded?: (assetId: string, pageIndex: number) => void;
  /** Fired when the server settled a page on a different index than the one
   *  sent (cross-device collision bump). Both indexes are the wizard's local
   *  page numbers; `to` is what the server now holds. */
  onPageIndexReassigned?: (from: number, to: number) => void;
  maxAttempts?: number;
  /** Base backoff (doubles per attempt). Injectable for tests. */
  backoffMs?: number;
  /** Timer seam for tests. */
  setTimeoutFn?: (fn: () => void, ms: number) => unknown;
}

interface PagePair {
  pageIndex: number;
  renditionTaskId: string | null;
  originalTaskId: string;
  original: Blob;
  rendition: Blob | null;
  renditionAssetId: string | null;
  source: 'camera' | 'upload';
}

let taskSeq = 0;

export class CaptureUploader {
  private deps: Required<Pick<UploaderDeps, 'upload'>> & UploaderDeps;
  private tasks = new Map<string, UploadTask>();
  private pairs = new Map<number, PagePair>();
  private blobs = new Map<string, Blob>();
  private order: string[] = [];

  constructor(deps: UploaderDeps) {
    this.deps = { maxAttempts: 3, backoffMs: 800, ...deps };
  }

  /**
   * Enqueue one captured document page. The rendition upload starts
   * immediately; the original starts as soon as the rendition's asset id
   * exists (it needs `link_asset_id`). If the rendition fails terminally, the
   * original uploads unlinked — the server tolerates original-first arrival
   * and back-links whichever lands second.
   */
  captureDocumentPage(input: {
    original: Blob;
    rendition: Blob | null;
    pageIndex: number;
    source?: 'camera' | 'upload';
  }): { renditionTaskId: string | null; originalTaskId: string } {
    const source = input.source ?? 'camera';
    const pageNo = input.pageIndex + 1;
    let renditionTaskId: string | null = null;
    const originalTaskId = this.addTask({
      label: `Page ${pageNo} (original)`,
      tier: 'original',
      kind: 'ixrs_photo',
      pageIndex: input.pageIndex,
    });
    this.blobs.set(originalTaskId, input.original);

    if (input.rendition) {
      renditionTaskId = this.addTask({
        label: `Page ${pageNo}`,
        tier: 'rendition',
        kind: 'ixrs_photo',
        pageIndex: input.pageIndex,
      });
      this.blobs.set(renditionTaskId, input.rendition);
    }

    this.pairs.set(input.pageIndex, {
      pageIndex: input.pageIndex,
      renditionTaskId,
      originalTaskId,
      original: input.original,
      rendition: input.rendition,
      renditionAssetId: null,
      source,
    });

    if (renditionTaskId) {
      void this.run(renditionTaskId);
    } else {
      // No rendition (shouldn't happen for camera captures) — original goes alone.
      void this.run(originalTaskId);
    }
    return { renditionTaskId, originalTaskId };
  }

  /** All tasks, insertion-ordered (chip strip). */
  list(): UploadTask[] {
    return this.order.map((id) => ({ ...this.tasks.get(id)! }));
  }

  /** Originals not yet stored — attestation blocks while > 0 (R12). */
  pendingOriginalCount(): number {
    return this.list().filter((t) => t.tier === 'original' && t.status !== 'done').length;
  }

  /** Any task in a terminal error state (manual retry needed). */
  hasErrors(): boolean {
    return this.list().some((t) => t.status === 'error');
  }

  /** Manual retry for a task that exhausted its automatic attempts. */
  retry(taskId: string): void {
    const task = this.tasks.get(taskId);
    if (!task || task.status !== 'error') return;
    task.attempts = 0;
    task.error = null;
    void this.run(taskId);
  }

  // -- internals ------------------------------------------------------------

  /** Move every task + the pair for local page `from` onto `to` (server-settled). */
  private remapPage(from: number, to: number): void {
    for (const t of this.tasks.values()) {
      if (t.pageIndex !== from) continue;
      t.pageIndex = to;
      t.label = `Page ${to + 1}${t.tier === 'original' ? ' (original)' : ''}`;
    }
    const pair = this.pairs.get(from);
    if (pair) {
      this.pairs.delete(from);
      pair.pageIndex = to;
      this.pairs.set(to, pair);
    }
    this.deps.onPageIndexReassigned?.(from, to);
  }

  private addTask(input: { label: string; tier: UploadTier; kind: 'ixrs_photo' | 'ixrs_document'; pageIndex: number }): string {
    const id = `up-${++taskSeq}`;
    this.tasks.set(id, {
      id,
      label: input.label,
      tier: input.tier,
      kind: input.kind,
      status: 'queued',
      attempts: 0,
      assetId: null,
      pageIndex: input.pageIndex,
      error: null,
    });
    this.order.push(id);
    this.emit();
    return id;
  }

  private emit(): void {
    this.deps.onChange?.(this.list());
  }

  private schedule(fn: () => void, ms: number): void {
    const st = this.deps.setTimeoutFn ?? ((f: () => void, m: number) => setTimeout(f, m));
    st(fn, ms);
  }

  private async run(taskId: string): Promise<void> {
    const task = this.tasks.get(taskId);
    const blob = this.blobs.get(taskId);
    if (!task || !blob || task.status === 'done' || task.status === 'uploading') return;
    task.status = 'uploading';
    task.attempts += 1;
    task.error = null;
    this.emit();

    const pair = this.pairs.get(task.pageIndex);
    try {
      const linkAssetId = task.tier === 'original' ? pair?.renditionAssetId ?? undefined : undefined;
      const result = await this.deps.upload(blob, {
        tier: task.tier,
        kind: task.kind,
        page_index: task.pageIndex,
        ...(linkAssetId ? { link_asset_id: linkAssetId } : {}),
        filename: `page-${task.pageIndex + 1}${task.tier === 'rendition' ? '-rendition' : ''}.jpg`,
        source: pair?.source ?? 'camera',
      });
      task.status = 'done';
      task.assetId = result.asset.id;
      this.blobs.delete(taskId); // free memory; retries are over
      // The server may have settled this page on another index (cross-device
      // collision bump). Remap BEFORE announcing, so every callback and the
      // strip see the effective number.
      const effective = result.asset.page_index;
      if (Number.isInteger(effective) && effective !== task.pageIndex) {
        this.remapPage(task.pageIndex, effective as number);
      }
      this.emit();

      if (task.tier === 'rendition') {
        if (pair) pair.renditionAssetId = result.asset.id;
        this.deps.onRenditionUploaded?.(result.asset.id, task.pageIndex);
        // The original follows its rendition (now linkable).
        if (pair) void this.run(pair.originalTaskId);
      } else {
        this.deps.onOriginalUploaded?.(result.asset.id, task.pageIndex);
      }
    } catch (err: any) {
      const failedTier = task.tier;
      if (task.attempts < (this.deps.maxAttempts ?? 3)) {
        task.status = 'queued';
        task.error = null;
        this.emit();
        const delay = (this.deps.backoffMs ?? 800) * 2 ** (task.attempts - 1);
        this.schedule(() => void this.run(taskId), delay);
      } else {
        task.status = 'error';
        task.error = err?.message || 'Upload failed';
        this.emit();
        // Terminal rendition failure must not strand the original (R12: the
        // evidence still has to land) — send it unlinked.
        if (failedTier === 'rendition' && pair) void this.run(pair.originalTaskId);
      }
    }
  }
}
